import { useMemo } from "react";

import clsx from "clsx";
import { compact, uniq, uniqBy } from "lodash";

import { Checkbox, PrimaryButton } from "@components/v2";
import {
  createColumnHelper,
  HeaderWithInfoIcon,
  Table,
} from "@components/v2/table";
import { useUniversalBenchmarksMetrics } from "@features/universal-benchmarks";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import { type SalesMetricRow } from "@north-beam/nb-common";
import { type CellContext } from "@tanstack/react-table";

import {
  useSalesMetricsCountQuery,
  useSalesMetricsReportQuery,
  useSalesV4Context,
  useTableStateDimensions,
} from "../hooks";
import { useSalesTableColumnWidths } from "../sales-v4-provider/hooks/use-sales-table-column-widths";
import { type TableState } from "../types";
import {
  buildAdvancedSearchJson,
  getSelectionForSelectedTab,
  getSelectionStateForSelectedTab,
  selectedTabToLevel,
} from "../utils";

import { BreakdownNameColumn } from "./cell-breakdown-name";
import { CellSalesContentCell } from "./cell-sales-content-cell";
import { CellStatus } from "./cell-status";

export type Props = {
  tableState: TableState;
  className?: string;
  onTableStateChange: (tableState: TableState) => void;
  isMaximized?: boolean;
};

export const SalesTable = ({
  tableState,
  className,
  onTableStateChange,
  isMaximized,
}: Props) => {
  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();
  const {
    metrics,
    dimensions,
    dateRangeInterval,
    compareDateRangeInterval,
    timeGranularity,
    selectedQuickFilter,
    setTableStates,
  } = useSalesV4Context();

  const {
    dimensionsToSelect,
    idColumn,
    nameColumn,
    breakdownDimension,
    statusDimension,
  } = useTableStateDimensions(tableState);

  const selectedDimensions = useMemo(() => {
    return compact(
      tableState.selectedMetricIds.map((id) =>
        dimensions.find((dimension) => `dimension:${dimension.id}` === id),
      ),
    );
  }, [dimensions, tableState.selectedMetricIds]);

  const selectedMetrics = useMemo(() => {
    return compact(
      tableState.selectedMetricIds.map((id) =>
        metrics.find((metric) => metric.id === id),
      ),
    );
  }, [metrics, tableState.selectedMetricIds]);

  const { metricsV2: benchmarkMetrics } = useUniversalBenchmarksMetrics({
    attributionModel,
    attributionWindow,
    accountingMode,
  });

  const sharedVars = useMemo(() => {
    const breakdownFilters = [];
    const campaignFilters = [];
    const adsetFilters = [];
    let advancedSearch: any = buildAdvancedSearchJson(
      tableState.searchText,
      nameColumn,
    );

    if (tableState.selectedTab === "breakdown") {
      breakdownFilters.push(...tableState.breakdownFilters);
    }

    if (["campaign", "adset", "ad"].includes(tableState.selectedTab)) {
      if (breakdownDimension.id === "date") {
        breakdownFilters.push(...tableState.breakdownFilters);

        if (tableState.breakdownSelection.length > 0) {
          const selection = {
            or: tableState.breakdownSelection.map((e) => ({
              "==": [{ var: breakdownDimension.id }, e.id],
            })),
          };

          advancedSearch = {
            and: compact([advancedSearch, selection]),
          };
        }
      } else {
        const selection = tableState.breakdownSelection.map((e) => ({
          breakdown: breakdownDimension.name,
          value: e.id,
        }));

        breakdownFilters.push(...selection);

        for (const filter of tableState.breakdownFilters) {
          if (!selection.some((e) => e.breakdown === filter.breakdown)) {
            breakdownFilters.push(filter);
          }
        }
      }
    }

    if (["adset", "ad"].includes(tableState.selectedTab)) {
      campaignFilters.push(...tableState.campaignSelection.map((e) => e.id));
    }

    if (tableState.selectedTab === "ad") {
      adsetFilters.push(...tableState.adsetSelection.map((e) => e.id));
    }

    const availableMetricIds = metrics.map((e) => e.id);

    const metricIds = uniq([
      ...tableState.selectedMetricIds,
      ...benchmarkMetrics.map((e) => e.id),
    ])
      .filter((metricId) => availableMetricIds.includes(metricId))
      .sort();

    return {
      attributionModel,
      attributionWindow,
      accountingMode,
      dateRange: dateRangeInterval,
      level: selectedTabToLevel(tableState.selectedTab),
      timeGranularity,
      metricFilters: tableState.metricFilters,
      advancedSearch,
      breakdownFilters: uniqBy(
        breakdownFilters,
        (e) => `${e.breakdown}${e.value}`,
      ),
      campaignFilters,
      adsetFilters,
      metricIds,
      dimensionIds: dimensionsToSelect.map((e) => e.id).sort(),
    };
  }, [
    metrics,
    attributionModel,
    attributionWindow,
    accountingMode,
    dateRangeInterval,
    timeGranularity,
    tableState,
    dimensionsToSelect,
    breakdownDimension,
    nameColumn,
    benchmarkMetrics,
  ]);

  const { rows: mainRowsData, loading: loadingReport } =
    useSalesMetricsReportQuery({
      skip: tableState.selectedMetricIds.length === 0,
      queryName: "Sales Table Rows",
      variables: {
        ...sharedVars,
        compareDateRange:
          nameColumn.id === "date" ? undefined : compareDateRangeInterval,
        sorting: tableState.sorting
          .filter(
            (s) =>
              s.id !== "status" ||
              (s.id === "status" &&
                dimensionsToSelect.map((e) => e.id).includes(s.id)),
          )
          .map((e) => {
            if (e.id === "name") {
              return {
                dimensionId: nameColumn.id,
                order: e.desc ? "desc" : "asc",
              };
            }

            if (e.id === "status") {
              return {
                dimensionId: statusDimension.id,
                order: e.desc ? "desc" : "asc",
              };
            }

            return { metricId: e.id, order: e.desc ? "desc" : "asc" };
          }),
        limit: tableState.pageSize,
        offset: tableState.pageIndex * tableState.pageSize,
      },
    });

  const { rows: summaryRowsData, loading: loadingSummaryReport } =
    useSalesMetricsReportQuery({
      skip: tableState.selectedMetricIds.length === 0,
      queryName: "Sales Table Summary",
      variables: {
        ...sharedVars,
        compareDateRange:
          nameColumn.id === "date" ? undefined : compareDateRangeInterval,
        isSummary: true,
      },
    });

  const { total, loading: loadingCount } = useSalesMetricsCountQuery({
    skip: tableState.selectedMetricIds.length === 0,
    variables: sharedVars,
  });

  const mainRows = useMemo(() => {
    return mainRowsData.actualRows.map((r) => {
      const actualRowId = idColumn.getValue(r);
      const comparisonRow = mainRowsData.comparisonRows.find(
        (r) => idColumn.getValue(r) === actualRowId,
      );

      return { ...r, comparisonMetrics: comparisonRow?.metrics ?? {} };
    });
  }, [mainRowsData, idColumn]);

  const summaryRows = useMemo(() => {
    const actualRow = summaryRowsData.actualRows[0];
    const comparisonRow = summaryRowsData.comparisonRows[0];

    if (actualRow) {
      const isFilteredByDateBreakdown =
        breakdownDimension.id === "date" &&
        tableState.breakdownSelection.length > 0;

      return [
        {
          ...actualRow,
          comparisonMetrics: isFilteredByDateBreakdown
            ? {}
            : comparisonRow?.metrics ?? {},
        },
      ];
    } else {
      return [];
    }
  }, [summaryRowsData, breakdownDimension, tableState]);

  const allRows = useMemo(() => {
    return mainRows.length > 0 ? [...summaryRows, ...mainRows] : [];
  }, [mainRows, summaryRows]);

  const columnHelperWrapper = createColumnHelper<SalesMetricRow>();

  const columns = useMemo(
    () => [
      columnHelperWrapper.accessor({
        accessorKey: () => "select",
        columnDef: {
          id: "select",
          cell: (cell) => {
            if (cell.row.getIsPinned()) {
              return null;
            }

            return (
              <div
                className="h-full w-full flex-center cursor-pointer"
                onClick={cell.row.getToggleSelectedHandler()}
              >
                <Checkbox readOnly checked={cell.row.getIsSelected()} />
              </div>
            );
          },
          size: 40,
          maxSize: 40,
          header: "",
          sticky: true,
        },
      }),
      columnHelperWrapper.accessor({
        accessorKey: (r) => nameColumn.getValue(r),
        columnDef: {
          id: "name",
          cell: (cell: CellContext<SalesMetricRow, string>) => (
            <BreakdownNameColumn
              cell={cell}
              tableState={tableState}
              onTableStateChange={onTableStateChange}
              isMaximized={isMaximized}
            />
          ),
          header: (header) => (
            <HeaderWithInfoIcon title="Name" header={header} />
          ),
          size: 240,
          sticky: true,
          enableSorting: true,
        },
      }),
      ...selectedDimensions.map((column) =>
        columnHelperWrapper.accessor({
          accessorKey: (r) => column.getValue(r),
          columnDef: {
            id: column.id,
            cell: (cell: CellContext<SalesMetricRow, string>) => {
              if (column.id === "status") {
                return <CellStatus cell={cell} />;
              }

              return cell.getValue();
            },
            header: (header) => (
              <HeaderWithInfoIcon title={column.name} header={header} />
            ),
            size: 100,
            enableSorting: true,
          },
        }),
      ),
      ...selectedMetrics.map((metric) =>
        columnHelperWrapper.accessor({
          accessorKey: (r) => metric.getValue(r.metrics),
          columnDef: {
            id: metric.id,
            cell: (cell) => {
              return (
                <CellSalesContentCell
                  cell={cell}
                  metric={metric}
                  tableState={tableState}
                />
              );
            },
            header: (header) => (
              <HeaderWithInfoIcon
                title={metric.fullName}
                header={header}
                description={metric.description}
              />
            ),
            enableSorting: true,
            size: 120,
          },
        }),
      ),
    ],
    [
      columnHelperWrapper,
      selectedDimensions,
      selectedMetrics,
      nameColumn,
      tableState,
    ],
  );

  const { columnWidths, setColumnWidths } = useSalesTableColumnWidths();

  return (
    <div
      key={selectedQuickFilter.id}
      className={clsx(
        isMaximized ? "h-full overflow-hidden" : "flex flex-col",
        className,
      )}
    >
      <Table
        className="max-h-[calc(100%-56px)]"
        columns={columns}
        rows={allRows}
        pinnedTopRowIds={
          summaryRows.length > 0 && mainRows.length > 0 ? ["pinnedTopRow"] : []
        }
        noDataContent={
          <div
            className={clsx(
              "text-v2-primary text-sm font-semibold tracking-wide flex flex-col items-center",
            )}
          >
            No data available
            <PrimaryButton
              onClick={() => {
                setTableStates([
                  {
                    ...tableState,
                    selectedTab: tableState.selectedTab,
                    searchText: "",
                    selectedBreakdownId:
                      selectedQuickFilter.config.breakdownIds[0],
                    breakdownFilters:
                      selectedQuickFilter.config.breakdownFilters,
                    metricFilters: [],
                    breakdownSelection: [],
                    campaignSelection: [],
                    adsetSelection: [],
                    adSelection: [],
                  },
                ]);
              }}
              className="mt-4"
            >
              Reset
            </PrimaryButton>
          </div>
        }
        getRowId={(row) => idColumn.getValue(row) ?? "pinnedTopRow"}
        rowCount={total}
        multiSort={false}
        shouldHighlightOnHover
        columnWidths={columnWidths}
        onColumnWidthsChange={setColumnWidths}
        loading={loadingReport || loadingSummaryReport || loadingCount}
        rowSelection={getSelectionForSelectedTab(tableState).reduce(
          (acc, row) => {
            acc[row.id] = true;
            return acc;
          },
          {} as Record<string, boolean>,
        )}
        onRowSelectionChange={(selectedIds) => {
          const idToLabel: Record<string, string> = {};

          for (const row of allRows) {
            const id = idColumn.getValue(row) ?? "";
            const label = nameColumn.getValue(row) ?? "";
            idToLabel[id] = label;
          }

          onTableStateChange(
            getSelectionStateForSelectedTab(
              tableState,
              selectedIds.map((rowId) => ({
                id: rowId,
                label: idToLabel[rowId],
              })),
            ),
          );
        }}
        pagination={{
          pageIndex: tableState.pageIndex,
          pageSize: tableState.pageSize,
        }}
        onPaginationChange={(pagination) => {
          onTableStateChange({
            ...tableState,
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
          });
        }}
        sortingState={compact(tableState.sorting)}
        onSortingChange={(sorting) => {
          onTableStateChange({ ...tableState, sorting });
        }}
      />
    </div>
  );
};
